import React, { Component } from "react";
import { message, Spin, notification } from "antd";
import { navigate } from "@reach/router";
import DashboardLayout from "./DashboardLayout";

import { AuthService } from "../../services/AuthService.ts";
import { fetchUserAuthContext } from "../../utils/authRedirect";
import {
  ROLE_AFFILIATE,
  getDashboardPathForRole,
  isPathAllowedForRole,
} from "../../utils/roles";
import {
  AFFILIATES_AGENCY_VIEW,
  buildViewsAllowedFromAuth,
  fetchAuthorizationData,
  getPermissionsForView,
  isViewAllowed,
} from "../../utils/permissions";
import { captureException } from "../../utils/sentry";

const authService = new AuthService();

const getDeniedViewRedirectPath = (roleID, userId, agencyID) => {
  if (roleID === ROLE_AFFILIATE) {
    return getDashboardPathForRole(roleID, userId, agencyID);
  }

  return "/";
};

class DashboardWrapper extends Component {
  state = {
    user: null,
    roleID: null,
    agencyID: null,
    viewsAllowed: null,
    resolvingRole: false,
  };

  componentDidMount() {
    this.getUser();
  }

  getPermissionsView = viewName => {
    return getPermissionsForView(viewName, this.state.viewsAllowed);
  };

  isAllowedView = viewName => {
    if (!isViewAllowed(viewName, this.state.viewsAllowed)) {
      notification.error({
        message: "No tiene los permisos para ver este contenido.",
      });
      navigate(
        getDeniedViewRedirectPath(
          this.state.roleID,
          this.state.user?.profile?.sub,
          this.state.agencyID
        )
      );
      return false;
    }

    return true;
  };

  loadUserPermissions = async user => {
    const authData = await fetchAuthorizationData(user.access_token);
    return buildViewsAllowedFromAuth(authData, user.profile);
  };

  resolveRoleAndGuardRoute = async (user, viewsAllowed) => {
    const { client, location } = this.props;
    const userId = user.profile.sub;

    this.setState({ resolvingRole: true });

    try {
      const authContext = await fetchUserAuthContext(client, userId);

      if (!authContext || authContext.roleID == null) {
        message.error("No se pudo obtener el rol del usuario.");
        navigate("/");
        return;
      }

      const { roleID, agencyID } = authContext;

      if (roleID !== ROLE_AFFILIATE && !agencyID) {
        message.error("No se encontró la agencia asociada al usuario.");
        navigate("/");
        return;
      }

      const currentUri = location.pathname || window.location.pathname;
      const isAgencyAffiliatesRoute = currentUri.includes(
        `/dashboard/affiliatesAgency/detail/`
      );

      if (
        isAgencyAffiliatesRoute &&
        !isViewAllowed(AFFILIATES_AGENCY_VIEW, viewsAllowed)
      ) {
        notification.error({
          message: "No tiene los permisos para ver este contenido.",
        });
        navigate(getDeniedViewRedirectPath(roleID, userId, agencyID));
        return;
      }

      if (!isPathAllowedForRole(currentUri, roleID, userId, agencyID)) {
        navigate(getDashboardPathForRole(roleID, userId, agencyID));
      }

      this.setState({
        user,
        roleID,
        agencyID,
        viewsAllowed,
        resolvingRole: false,
      });
    } catch (error) {
      captureException(error);
      message.error("Error al verificar el rol del usuario.");
      navigate("/");
      this.setState({ resolvingRole: false });
    }
  };

  handleAuthenticatedUser = async user => {
    if (!user) {
      navigate("/");
      return;
    }

    try {
      const viewsAllowed = await this.loadUserPermissions(user);
      await this.resolveRoleAndGuardRoute(user, viewsAllowed);
    } catch (error) {
      captureException(error);
      message.error("Error al cargar los permisos del usuario.");
      navigate("/");
    }
  };

  getUser = async () => {
    localStorage.setItem("redirectUrl", this.props.location.href);

    try {
      const userData = await authService.getUser();

      if (!userData) {
        authService.renewToken().then(
          user => {
            if (user) {
              message.success("Usuario autenticado.");
              this.handleAuthenticatedUser(user);
            } else {
              navigate("/");
            }
          },
          () => navigate("/")
        );
        return;
      }

      message.success("Usuario autenticado.");
      await this.handleAuthenticatedUser(userData);
    } catch {
      navigate("/");
    }
  };

  render() {
    const { children } = this.props;
    const { user, roleID, agencyID, viewsAllowed, resolvingRole } = this.state;
    const isReady = user && viewsAllowed && roleID != null && !resolvingRole;

    return (
      <div>
        {isReady ? (
          <DashboardLayout
            authUser={user}
            roleID={roleID}
            agencyID={agencyID}
            viewsAllowed={viewsAllowed}
            getPermissionsView={this.getPermissionsView}
            isAllowedView={this.isAllowedView}
          >
            {children}
          </DashboardLayout>
        ) : (
          <div style={{ width: "100%" }}>
            <Spin tip="Cargando... " />
          </div>
        )}
      </div>
    );
  }
}

export default DashboardWrapper;
